import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../stores/store.ts";
import StringHelper from "../../helpers/stringHelper.ts";
import DateHelper from "../../helpers/dateHelper.ts";
import { epgPageActions } from "../epg/epgPageSlice.ts";
import { useDebounce } from "../../hooks/useDebounce.ts";
import { IEpgSegment, ITimelineHour } from "../../interfaces/IEpgPageState.ts";

interface ChangeDataTimeline {
    name: string;
    timestamp: number;
}

const EpgPageTimeLine = () => {
    const dispatch = useAppDispatch();
    const epgStartTimestamp = useAppSelector(state => state.epgPage.epgStartTimestamp);
    const epgEndTimestamp = useAppSelector(state => state.epgPage.epgEndTimestamp);
    const epgLastEpgDownloadPointTimestamp = useAppSelector(state => state.epgPage.epgLastDownloadPointTimestamp);
    const epgActualRangeFromTimestamp = useAppSelector(state => state.epgPage.epgActualRangeFromTimestamp);
    const epgActualRangeToTimestamp = useAppSelector(state => state.epgPage.epgActualRangeToTimestamp);
    const timelineHours = useAppSelector(state => state.epgPage.timelineHours);
    const isTimelineLoaded = useAppSelector(state => state.epgPage.isTimelineLoaded);
    const changeDateTimelines = useAppSelector(state => state.epgPage.changeDateTimelines);
    const [isInitialRunComplete, setIsInitialRunComplete] = useState(false);

    const [epgActualRangeFromTimestampTemp, setEpgActualRangeFromTimestampTemp] = useState(0);
    const [epgActualRangeToTimestampTemp, setEpgActualRangeToTimestampTemp] = useState(0);
    const debounceEpgActualRangeFromTimestamp = useDebounce(epgActualRangeFromTimestampTemp, 500);
    const debounceEpgActualRangeToTimestamp = useDebounce(epgActualRangeToTimestampTemp, 500);
    const [nowBadgeDisplayMode, setNowBadgeDisplayMode] = useState<"normal" | "leftBadge" | "rightBadge">("normal");

    const [elapsedTimeMaskWidth, setElapsedTimeMaskWidth] = useState(0);

    const getMaskWidthToActualTime = (fromTimestamp: number, hourInPixel: number): number => {
        const iconLogoWidth = 64;
        const dateTimeNow = new Date();
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
        const currentUnixTimestamp = Math.floor(dateTimeNow.getTime() / 1000);

        return (Math.ceil(
            ((currentUnixTimestamp - fromTimestamp) / 60) *
            (1 / (60 / hourInPixel)) +
            iconLogoWidth + 6
        ));
    };


    const [intersectionTimestamp, setIntersectionTimestamp] = useState(0);

    const scrollEpgX = (scrollValue: number) => {
        const scroller = document.getElementById("scroller");
        scroller?.scrollTo({ left: scroller.scrollLeft + scrollValue, behavior: "smooth" });
    }

    const scrollToCurrentTime = () => {
        const nowBadge = document.getElementById("now-badge");
        if (nowBadge)
            nowBadge.scrollIntoView({ inline: "center", behavior: "smooth" })
    }

    const setSelectedChangeDateTimeLineItem = (timestamp: number) => {
        const changeDateTimeLineItems = changeDateTimelines.filter(x => x.timestamp <= timestamp);
        if (changeDateTimeLineItems.length > 0)
            dispatch(epgPageActions.setSelectedChangeDataTimeline(changeDateTimeLineItems[changeDateTimeLineItems.length - 1]));
    }

    useEffect(() => {
        dispatch(epgPageActions.setEpgActualRangeFromTimestamp(debounceEpgActualRangeFromTimestamp));
        dispatch(epgPageActions.setEpgActualRangeToTimestamp(debounceEpgActualRangeToTimestamp));
    }, [debounceEpgActualRangeFromTimestamp, debounceEpgActualRangeToTimestamp]);

    useEffect(() => {
        if (isTimelineLoaded && epgActualRangeFromTimestampTemp === 0) {
            setEpgActualRangeToTimestampTemp(epgActualRangeToTimestamp);
            setEpgActualRangeFromTimestampTemp(epgActualRangeFromTimestamp);
            let actualTimestampElem: Element | null;
            if (!isInitialRunComplete) {
                actualTimestampElem = document.querySelector(`[data-timestamp="${DateHelper.GetDateTimeNowWithMinutesAndSecundesSetOnZeroAsUnixTimestamp()}"]`);
                setIsInitialRunComplete(true);
            } else {
                actualTimestampElem = document.querySelector(`[data-timestamp="${epgActualRangeFromTimestamp}"]`);
            }
            if (actualTimestampElem)
                actualTimestampElem.scrollIntoView({ inline: "center" });
        }
        return () => {
            setIsInitialRunComplete(false);
        }
    }, [isTimelineLoaded, epgActualRangeFromTimestamp, epgActualRangeFromTimestampTemp, isInitialRunComplete]);

    useEffect(() => {
        const nowBadgeActual = document.getElementById("now-badge");
        const scrollerElem = document.getElementById("scroller");

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setNowBadgeDisplayMode("normal");
                } else {
                    if (entry.rootBounds && entry.boundingClientRect.left < entry.rootBounds.left) {
                        setNowBadgeDisplayMode("leftBadge")
                    } else if (entry.rootBounds && entry.boundingClientRect.right >= entry.rootBounds.right) {
                        setNowBadgeDisplayMode("rightBadge")
                    }
                }
            });
        }, {
            root: scrollerElem, // Obserwujemy względem viewportu (domyślnie)
            rootMargin: '0px -84px 0px -73px', // Margines wokół root (viewportu)
            threshold: [1] // Kiedy 10% elementu jest widoczne
        });
        if (isTimelineLoaded)
            if (nowBadgeActual)
                observer.observe(nowBadgeActual);

        return () => {
            if (nowBadgeActual)
                observer.observe(nowBadgeActual);
        }
    }, [isTimelineLoaded])

    useEffect(() => {
        let hoursElem: NodeListOf<Element>;
        let observerTimeline: IntersectionObserver;
        setTimeout(() => {
            hoursElem = window.document.querySelectorAll(".hour-timeline");
            observerTimeline = new IntersectionObserver((entries) => {
                const entriesOnlyIsIntersecting = entries.filter((x) => x.isIntersecting);
                const entriesElemIsIntersecting =
                    entriesOnlyIsIntersecting[entriesOnlyIsIntersecting.length - 1];
                if (entriesElemIsIntersecting && entriesElemIsIntersecting.target)
                    setIntersectionTimestamp(Number(entriesElemIsIntersecting.target.attributes.getNamedItem("data-timestamp")?.value));
            });
            hoursElem.forEach((elem) => {
                observerTimeline.observe(elem);
            });
        }, 2000)
        return () => {
            if (hoursElem)
                hoursElem.forEach((elem) => {
                    observerTimeline.unobserve(elem);
                });
        };
    }, [timelineHours]);

    useEffect(() => {
        const date = DateHelper.UnixTimestampToDateTime(epgStartTimestamp);
        if (date.getHours() !== 0) {
            date.setDate(date.getDate() + 1);
            date.setHours(0, 0, 0, 0);
        }

        // zmienne dla zmiany czasy (zimowy, letni)
        let hasDaylightSavingTimeChangedInLast7Days = false;
        let lastDSTChangeTimestampAndType;
        //
        // uruchamiamy logike w miesiacach 3,4,10,11 dla sprawdzania czy w miesiacu nastapila zmiana czasu (zimowy, letni)
        const monthNow = new Date().getMonth() + 1;
        if (monthNow === 10 || monthNow === 11 || monthNow === 3 || monthNow === 4) {
            hasDaylightSavingTimeChangedInLast7Days = DateHelper.hasDaylightSavingTimeChangedLast7Days();
            if (hasDaylightSavingTimeChangedInLast7Days) {
                lastDSTChangeTimestampAndType = DateHelper.getLastDSTTransitionTimestampAndType();
            }
        }
        //

        const startDateTimestamp = DateHelper.DateTimeToUnixTimestamp(date);
        let hoursCount = Math.floor((epgEndTimestamp - startDateTimestamp) / 3600);
        if (epgStartTimestamp > 0 && epgEndTimestamp > 0) {
            const dateNowUnixTimestamp = DateHelper.DateNowUnixTimestamp();
            let actualTimestamp = startDateTimestamp;
            const changeDateTimeLineArrayTemp: ChangeDataTimeline[] = [];
            const hoursArrayTemp: ITimelineHour[] = [];
            const epgSegmentsTemp: IEpgSegment[] = [];
            let segmentIndex = 0;

            for (let i = 0; i < hoursCount; i++) {
                // sekcja odpowiedzialna za ustawienie odpowiednich godzin przy zmianie czasu zimowy/letni
                if (hasDaylightSavingTimeChangedInLast7Days) {
                    if (lastDSTChangeTimestampAndType) {
                        if (lastDSTChangeTimestampAndType.changeToType === "winter" && lastDSTChangeTimestampAndType.unixTimestamp - 3600 === actualTimestamp) {
                            hoursArrayTemp.push({
                                hour: `02:00`,
                                timestamp: actualTimestamp,
                                downloadPoint: false,
                            });
                            actualTimestamp += 3600;
                        }
                    }
                }
                // koniec sekcji odpowiedzialnej za zmiane czasu

                hoursArrayTemp.push({
                    hour: `${i % 24}:00`,
                    timestamp: actualTimestamp,
                    downloadPoint: i % import.meta.env.VITE_EPG_PAGE_SEGMENT_WIDTH_IN_HOURS === 0 && actualTimestamp < epgLastEpgDownloadPointTimestamp
                });
                if (i % 24 == 0) {
                    let changeDateName = "";
                    if (actualTimestamp === dateNowUnixTimestamp - 24 * 3600) changeDateName = "Wczoraj";
                    else if (actualTimestamp === dateNowUnixTimestamp) changeDateName = "Dziś";
                    else if (actualTimestamp === dateNowUnixTimestamp + 24 * 3600) changeDateName = "Jutro";
                    else
                        changeDateName = StringHelper.changeFirstLatterToUpperCase(
                            DateHelper.GetDayNameFromUnixTimespanDate(actualTimestamp)
                        );
                    changeDateTimeLineArrayTemp.push({
                        name: changeDateName,
                        timestamp: actualTimestamp,
                    });
                }
                if (i % import.meta.env.VITE_EPG_PAGE_SEGMENT_WIDTH_IN_HOURS == 0 && actualTimestamp < epgLastEpgDownloadPointTimestamp) {
                    const newEpgSegment: IEpgSegment = {
                        id: segmentIndex,
                        fromTimestamp: actualTimestamp,
                        toTimestamp: actualTimestamp + import.meta.env.VITE_EPG_PAGE_SEGMENT_WIDTH_IN_HOURS * 3600,
                        isSegmentDownloaded: false
                    }
                    epgSegmentsTemp.push(newEpgSegment);
                    segmentIndex++;
                }
                actualTimestamp = actualTimestamp + 3600;
            }

            dispatch(epgPageActions.setEpgSegments(epgSegmentsTemp));
            dispatch(epgPageActions.setChangeDateTimelines(changeDateTimeLineArrayTemp))
            dispatch(epgPageActions.setTimelineHours(hoursArrayTemp));
            dispatch(epgPageActions.setIsTimelineLoaded());
            const todayChangeDateTimeLineItem = changeDateTimeLineArrayTemp.find(x => x.timestamp === dateNowUnixTimestamp);
            if (todayChangeDateTimeLineItem)
                dispatch(epgPageActions.setSelectedChangeDataTimeline(todayChangeDateTimeLineItem));
        }
    }, [epgStartTimestamp, epgEndTimestamp, epgLastEpgDownloadPointTimestamp]);

    useEffect(() => {
        if (isTimelineLoaded && epgActualRangeFromTimestamp > 0 && intersectionTimestamp > 0) {
            const epgListWidthInHours = Math.ceil(window.innerWidth / import.meta.env.VITE_EPG_PAGE_TIMELINE_HOUR_WIDTH_IN_PIXELS);
            let newEpgRangeFromDateTimeUnixTimestamp = 0;
            let newEpgRangeToDateTimeUnixTimestamp = 0;
            if (intersectionTimestamp >= epgActualRangeToTimestamp - 7200 && epgEndTimestamp > 0) {
                dispatch(epgPageActions.setChangeDirection("right"));
                newEpgRangeFromDateTimeUnixTimestamp =
                    intersectionTimestamp - epgListWidthInHours * 3600 - 3 * 3600;
                newEpgRangeToDateTimeUnixTimestamp = intersectionTimestamp + 3 * 3600;
                setSelectedChangeDateTimeLineItem(intersectionTimestamp - epgListWidthInHours * 3600);
            }

            if (
                intersectionTimestamp <= epgActualRangeFromTimestamp + 3600 &&
                epgActualRangeToTimestamp > 0
            ) {
                dispatch(epgPageActions.setChangeDirection("left"));
                newEpgRangeFromDateTimeUnixTimestamp = intersectionTimestamp - 3 * 3600;
                newEpgRangeToDateTimeUnixTimestamp =
                    intersectionTimestamp + (epgListWidthInHours + 2) * 3600;
                setSelectedChangeDateTimeLineItem(intersectionTimestamp);
            }
            if (newEpgRangeFromDateTimeUnixTimestamp !== 0) {
                if (epgStartTimestamp > newEpgRangeFromDateTimeUnixTimestamp) {
                    setEpgActualRangeFromTimestampTemp(epgStartTimestamp);
                } else {
                    setEpgActualRangeFromTimestampTemp(newEpgRangeFromDateTimeUnixTimestamp);
                }
            }
            if (newEpgRangeToDateTimeUnixTimestamp)
                setEpgActualRangeToTimestampTemp(newEpgRangeToDateTimeUnixTimestamp);
        }
    }, [isTimelineLoaded, intersectionTimestamp, epgEndTimestamp, epgActualRangeFromTimestamp, epgStartTimestamp, epgActualRangeToTimestamp]);

    useEffect(() => {
        let interval: number;

        if (isTimelineLoaded) {
            interval = setInterval(() => {
                setElapsedTimeMaskWidth(getMaskWidthToActualTime(timelineHours[0].timestamp, import.meta.env.VITE_EPG_PAGE_TIMELINE_HOUR_WIDTH_IN_PIXELS));
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isTimelineLoaded, timelineHours])

    useEffect(() => {
        return () => {
            dispatch(epgPageActions.setInitialState());
        };
    }, []);

    return (
        <>
            {timelineHours.length > 0 &&
                <div
                    className={"z-40 sticky top-0 flex"}
                    id="timeline"
                    style={{ display: 'flex', flexDirection: 'row', width: "100%" }}
                    key={"timeline"}
                >
                    <div className="flex items-end relative">
                        <button
                            className={"sticky left-0 w-18 shrink-0 bg-neutral-800 flex justify-center items-center z-10"}
                            onClick={() => scrollEpgX(-1000)}
                        >
                            <ChevronLeftIcon className={"w-6 h-6 pb-px"} />
                        </button>
                        <div className="absolute top-[14px] flex flex-col"
                            style={{
                                left: `${elapsedTimeMaskWidth}px`,
                            }}>
                            <div className="relative -top-1">
                                <button
                                    id="now-badge"
                                    className={`text-xs bg-neutral-300 text-neutral-700 px-1 py-px rounded-sm ml-0 rounded-bl-none ${nowBadgeDisplayMode === "normal" ? "opacity-100" : "opacity-0"}`}
                                    onClick={scrollToCurrentTime}
                                >
                                    TERAZ
                                </button>
                                <div
                                    className={`absolute w-px left-0 bg-[#007CC1] ${nowBadgeDisplayMode !== "leftBadge" ? "opacity-100" : "opacity-0"}`}
                                    style={{
                                        height: `100vh`
                                    }} />
                            </div>
                        </div>
                        {nowBadgeDisplayMode === "leftBadge" &&
                            <button
                                className={`text-xs sticky left-18 bg-neutral-300 text-neutral-700 px-1 py-px rounded-sm ml-0 ${nowBadgeDisplayMode === "leftBadge" ? "opacity-100" : "opacity-0"}`}
                                onClick={scrollToCurrentTime}
                            >
                                TERAZ
                            </button>
                        }
                        <div className={`flex ${nowBadgeDisplayMode === "leftBadge" ? "-ml-11" : "ml-0"}`}>
                            {timelineHours.map((hourItem) => (
                                <div
                                    key={hourItem.timestamp}
                                    className={"hour-timeline flex h-8 justify-between items-end space-x-1 bg-neutral-800 select-none"}
                                    style={{ width: `${import.meta.env.VITE_EPG_PAGE_TIMELINE_HOUR_WIDTH_IN_PIXELS}px` }}
                                    data-timestamp={hourItem.timestamp}
                                    data-download-point={hourItem.downloadPoint}
                                >
                                    <div className={"flex items-end shrink space-x-1"}>
                                        <div className={"h-5 w-px bg-neutral-400"} />
                                        <div className={"bg-neutral-800"}>{hourItem.hour}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button
                            id="nowbadgeright"
                            className={`text-xs sticky right-20 bg-neutral-300 text-neutral-700 px-1 py-px rounded-sm ml-0 z-50 ${nowBadgeDisplayMode === "rightBadge" ? "opacity-100" : "opacity-0"}`}
                            onClick={scrollToCurrentTime}
                        >
                            TERAZ
                        </button>
                        <button
                            className={"sticky w-20 right-0 z-50 bg-neutral-800"}
                            id="scroll-timeline-right-btn"
                            onClick={() => scrollEpgX(1000)}
                        >
                            <ChevronRightIcon className={"w-6 h-6 pb-px mx-auto"} />
                        </button>
                    </div>
                </div>
            }
        </>
    );
};

export default React.memo(EpgPageTimeLine);